import { useCallback, useEffect, useState } from 'react';
import { PlusCircle, Send } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import Ayuda from '../ui/Ayuda';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';

const pesos = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

// LOS ESTADOS EN COLOR DE VERDAD: verde solo cuando el techo ya subió.
const TONO = {
  ENVIADA: 'bg-amber-50 text-amber-900 border-amber-200',
  COTIZADA: 'bg-amber-50 text-amber-900 border-amber-200',
  ACEPTADA: 'bg-amber-50 text-amber-900 border-amber-200',
  PAGADA: 'bg-amber-50 text-amber-900 border-amber-200',
  APLICADA: 'bg-green-50 text-green-800 border-green-200',
  CANCELADA: 'bg-gray-100 text-gray-700 border-gray-200',
};

function cuando(iso) {
  if (!iso) return 'sin fecha';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

function normalizarFrase(texto) {
  return String(texto || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * «Pedir más recursos» · SUPREMO Parte B. El nodo no cotiza: enseña el
 * catálogo que bajó de la nube, guarda lo que el Supremo pidió con su
 * frase y muestra la cotización que la nube contestó, con su fecha.
 */
export default function RecursosExtra() {
  const { tieneNivel } = useAuth();
  const esDueno = tieneNivel('DUENO');
  const [cat, setCat] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [error, setError] = useState(null);
  const [cantidades, setCantidades] = useState({});
  const [frase, setFrase] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [fallo, setFallo] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get('/recursos'), api.get('/recursos/solicitudes')]);
      setCat(c);
      setSolicitudes(s.solicitudes || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (error) return <ErrorConReintento que="No se pudo leer el catálogo de recursos" porque={error} onReintentar={cargar} />;
  if (!cat) return <Cargando filas={3} etiqueta="Leyendo el catálogo de recursos" />;
  // Lo que bajó puede venir a medias (una nube vieja): nada aquí revienta.
  const recursos = Array.isArray(cat.recursos) ? cat.recursos : [];

  const lineas = Object.entries(cantidades)
    .filter(([, n]) => Number(n) > 0)
    .map(([recurso, n]) => ({ recurso, cantidad: Number(n) }));
  const fraseBien = Boolean(cat.frase_esperada) && normalizarFrase(frase) === normalizarFrase(cat.frase_esperada);
  const porQueNo = !cat.puede_pedir
    ? 'La nube todavía no ha mandado el catálogo a este servidor: espera al siguiente aviso de vida.'
    : lineas.length === 0
      ? 'Pon una cantidad en al menos un recurso.'
      : !fraseBien
        ? 'Escribe la frase de confirmación tal cual aparece arriba.'
        : null;

  async function pedir() {
    setOcupado(true); setFallo(null); setAviso(null);
    try {
      const r = await api.post('/recursos/solicitar', { lineas, frase });
      setAviso(r.dice);
      setCantidades({}); setFrase('');
      await cargar();
    } catch (e) {
      setFallo(e instanceof ErrorApi ? e.message : 'No se pudo enviar la solicitud. Revisa la conexión y vuelve a intentarlo.');
    } finally { setOcupado(false); }
  }

  return (
    <div className="space-y-3 border-t border-gray-200 pt-4">
      <div className="flex items-center gap-2">
        <PlusCircle className="h-5 w-5 text-[var(--color-marca)]" />
        <span className="font-semibold text-gray-900">Pedir más recursos</span>
        <Ayuda id="consola.recursos.pantalla" />
      </div>
      <p className="text-xs text-gray-600">
        Precios según la nube {cat.leido_en ? `al ${cuando(cat.leido_en)}` : '(todavía sin leer)'}.
        {cat.politica?.reembolso ? ` ${cat.politica.reembolso}` : ''}
      </p>

      {recursos.length === 0 && (
        <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-xl p-3">
          Sin catálogo: llega en el siguiente aviso de vida de este servidor (unos diez minutos).
        </div>
      )}

      {recursos.length > 0 && (
        <ul className="space-y-1.5">
          {recursos.map((r) => (
            <li key={r.clave} className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">{r.nombre}</div>
                <div className="text-xs text-gray-500">
                  {Number(r.precio_mensual) === 0
                    ? 'Incluido en el plan'
                    : `${pesos.format(r.precio_mensual)}/mes por ${r.unidad}${r.lleva_iva ? ' + IVA' : ''}`}
                </div>
              </div>
              {esDueno && (
                <input
                  type="number" min="0" max="10000" inputMode="numeric"
                  aria-label={`Cantidad de ${r.nombre}`}
                  value={cantidades[r.clave] ?? ''}
                  onChange={(e) => setCantidades({ ...cantidades, [r.clave]: e.target.value })}
                  className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right"
                  placeholder="0"
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {esDueno && recursos.length > 0 && (
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-gray-600">
            Para confirmar, escribe: <span className="font-normal text-gray-900">«{cat.frase_esperada || '…'}»</span>
            <Ayuda id="consola.recursos.frase" />
            <input
              type="text" value={frase} onChange={(e) => setFrase(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder={cat.frase_esperada || 'Esperando a la nube…'}
              autoComplete="off"
            />
          </label>
          <button
            onClick={pedir}
            disabled={ocupado || Boolean(porQueNo)}
            className="w-full bg-[var(--color-marca)] text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <Send className="h-4 w-4" /> Pedir a mi proveedor de TEGO
          </button>
          {porQueNo && <p className="text-xs text-gray-600">{porQueNo}</p>}
          {aviso && <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2.5">{aviso}</div>}
          {fallo && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">{fallo}</div>}
        </div>
      )}

      {solicitudes.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-xs font-semibold text-gray-500 uppercase">Lo pedido</div>
          {solicitudes.map((s) => (
            <div key={s.id} className={`border rounded-xl px-3 py-2 text-sm ${TONO[s.estado] || TONO.ENVIADA}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{s.estado}</span>
                <span className="text-xs ml-auto">{cuando(s.creada_en)}</span>
              </div>
              <div className="text-xs">
                {(s.lineas || []).map((l) => `${l.cantidad} × ${l.recurso}`).join(', ')} · pidió {s.pedida_por}
              </div>
              <div className="text-xs">{cat.estados?.[s.estado] || ''}</div>
              {s.cotizacion && (
                <div className="text-xs mt-1">
                  Cotización de la nube: {pesos.format(s.cotizacion.total_mensual || 0)}/mes
                  {Number(s.cotizacion.total_a_pagar_ahora) > 0
                    ? ` · ahora ${pesos.format(s.cotizacion.total_a_pagar_ahora)} (${s.cotizacion.dias_restantes_del_ciclo} días del ciclo)`
                    : ' · sin costo: incluido en el plan'}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
